import type { SocketType } from '../types/socket'
import type { NodeCategory } from '../types/node'

/**
 * Socket 配色。
 *
 * 参考 Blender 的约定（按值「族」分色、饱和度偏低以免喧宾夺主），
 * 但色板是按本项目的类型系统重新设计的，不是 Blender 原始值的照搬。
 */
export const SOCKET_COLORS: Record<SocketType, string> = {
  number: '#A1A1A1', // 灰 —— 标量数值
  text: '#7EB6D9', // 蓝 —— 文本
  boolean: '#CCA640', // 暗黄 —— 布尔
  color: '#C7C729', // 亮黄 —— 颜色
  datetime: '#B36BE6', // 紫 —— 时间
  rich: '#E08A3C', // 橙 —— 富文本
  list: '#4DB8A8', // 青绿 —— 集合
  page: '#6E6EC7', // 靛 —— 页面
  component: '#5FA85F', // 绿 —— 组件/几何
  any: '#8A8A8A', // 中性灰 —— 通配
}

export function socketColor(type: SocketType | undefined): string {
  return (type && SOCKET_COLORS[type]) || SOCKET_COLORS.any
}

/**
 * **端口色的 CSS 变量引用**。
 *
 * ⚠️ 这个函数是「主题能不能改端口色」的关键，值得说清楚它换掉了什么。
 *
 * 改动前：`NpSocket.vue` 把 `socketColor(type)` 算出来的**十六进制字面量**
 * 内联成 `--np-socket-color`。值在 JS 模块加载时就固化了，CSS 够不到 ——
 * 所以浅色主题下端口颜色**一点没变**。
 *
 * 现在：JS 只拼出**一个变量引用**，真正的色值住在 `theme.css` 的
 * `--np-socket-<type>` 令牌里（主题可覆盖，一段任意 CSS 也能覆盖）。
 * JS 里**一个色值都不出现**了。
 *
 * 兜底链是两层的：先 `--np-socket-<type>`，再 `--np-socket-any`。
 * 第二层不能省 —— 将来加了新的端口类型而调色板还没跟上时，
 * 少了它这个变量就是**无效值**，整条 `border` 声明会被丢掉，
 * 端口直接消失（那种失败看起来像「节点坏了」）。
 */
export function socketColorVar(type: SocketType | undefined): string {
  const t = type ?? 'any'
  return `var(--np-socket-${t}, var(--np-socket-any))`
}

/*
 * ⚠️ `Record<string, string>` 而不是 `Record<NodeCategory, string>`：
 * 分类现在是**包提供的数据**，内核不知道全集。
 * 未知分类由 `categoryColorVar` 兜底到 `--np-cat-default` ——
 * 那种情况下类型系统帮不上忙，只能靠兜底值。
 */
export const CATEGORY_COLORS: Record<string, string> = {
  document: '#4A5D8A',
  page: '#55698A',
  data: '#5E5E5E',
  function: '#3F6E6E',
  component: '#4F7A4F',
  container: '#6B5A7A',
  utility: '#454545',
}

export function nodeCategoryColor(category: NodeCategory | undefined): string {
  return (category && CATEGORY_COLORS[category]) || CATEGORY_COLORS.utility
}

/**
 * **分类色的 CSS 变量引用**。同 `socketColorVar` 的道理。
 *
 * ⚠️ 分类是**开放集合**（包提供），所以没法像端口那样在 CSS 里逐个枚举，
 * 只能由 JS 拼变量名。但拼出来的仍然是**引用**，不是色值 ——
 * 主题只要声明 `--np-cat-<id>` 就能改。
 *
 * ⚠️ `--np-cat-default` 这一层兜底取代了原来的 `utility` 灰。
 * 旧行为下「装一个新节点包 → 它所有节点变同一个灰」，
 * 分类色这个信息直接没了。
 */
export function categoryColorVar(category: NodeCategory | undefined): string {
  if (!category) return 'var(--np-cat-default)'
  return `var(--np-cat-${category}, var(--np-cat-default))`
}

/*
 * ⚠️ 这里原来还有一份 `CATEGORY_LABELS`（中文分类名）——
 * 它是**死代码**：全仓库没有任何消费者，而真正的名字在
 * `domains/print/index.ts` 的 `PRINT_PACKAGE.categories` 里。
 * 三份分类名（这里 / useAddNode 的 CATEGORY_TITLES / NodeCategory 联合类型）
 * 现在只剩包里的那一份。
 */

/* ── 节点尺寸 ───────────────────────────────────────────────── */

/** 节点默认宽度 px */
export const DEFAULT_NODE_WIDTH = 180
/** 节点最小宽度 px —— 所有节点共用的统一基线 */
export const MIN_NODE_WIDTH = 156
